<template>

    <div class="flex items-center justify-between mb-8">
        <Head :title="supplier.name" />
        <page-title :title="$t('suppliers.supplierDetail')"></page-title>
    </div>

    <div class="my-2">
        <div class="md:flex bg-minplan-beige text-minplan-blue">
            <div class="flex w-full md:w-2/4">
                <div class="flex p-4 md:py-4 md:px-8 w-full">
                    <div>
                        <TruckIcon class="mr-2 h-5 w-5 text-minplan-darkgreybeige" />
                        <button type="button"
                                @click="$inertia.visitInModal(route('advisors.suppliers.edit.base', {supplier: supplier.uuid}))"
                                class="block">
                            <PencilIcon class="mr-2 mt-3 h-5 w-5 text-minplan-darkbeige hover:text-minplan-darkgreybeige" />
                        </button>
                    </div>
                    <div>
                        <span class="truncate block w-full font-bold">{{ $t('suppliers.supplier') }}</span>
                        {{ supplier.name }}<br />
                        {{ supplier.street }}<br />
                        {{ supplier.zip }} {{ supplier.city }}<br />
                        {{ supplier.country }}
                        <p class="text-minplan-darkbeige leading-5 pt-3">
                            {{ supplier.website }}<br v-if="supplier.website" />
                            {{ supplier.email }}<br v-if="supplier.email" />
                            {{ supplier.phone }}
                        </p>
                    </div>
                </div>
            </div>
            <div class="flex p-4 md:py-4 md:px-8 w-full md:w-2/4">
                <div>
                    <UserIcon class="mr-2 h-5 w-5 text-minplan-darkgreybeige" />
                    <button type="button"
                            @click="$inertia.visitInModal(route('advisors.suppliers.edit.contact', {supplier: supplier.uuid}))"
                    >
                        <PencilIcon class="mr-2 mt-2 h-5 w-5 text-minplan-darkbeige hover:text-minplan-darkgreybeige" />
                    </button>

                </div>
                <div>
                    <span class="truncate block w-full font-bold">{{ $t('suppliers.contacts') }}</span>
                    <p v-if="contactsWithFallback.length === 0" class="text-minplan-darkbeige text-sm">
                        {{ $t('suppliers.noContactsYet') }}
                    </p>
                    <p
                        v-for="contact in contactsWithFallback"
                        :key="contact.uuid"
                        class="text-minplan-darkbeige leading-5 mb-2"
                    >
                        {{ contact.firstname }} {{ contact.lastname }}<br />
                        <span v-if="contact.resolvedEmail">
                            {{ contact.resolvedEmail }}
                            <span v-if="contact.emailIsFallback" class="italic text-gray-400">
                                ({{ $t('suppliers.contactFallbackNotice') }})
                            </span>
                        </span>
                        <br v-if="contact.resolvedEmail" />
                        <span v-if="contact.resolvedPhone">
                            {{ contact.resolvedPhone }}
                            <span v-if="contact.phoneIsFallback" class="italic text-gray-400">
                                ({{ $t('suppliers.contactFallbackNotice') }})
                            </span>
                        </span>
                    </p>
                </div>
            </div>
        </div>
    </div>

    <!-- Info Bar Section -->
    <div class="my-2 pt-2 pb-3 flex justify-between items-center border-b">
        <div class="flex">
            <p class="flex items-center text-sm text-gray-500">
                <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full" :class="[supplier.state === 'active' ? 'bg-lime-100 text-lime-800' : 'bg-gray-100', 'text-gray-800']">
                    {{ $t("states." + supplier.state) }}
                </span>
            </p>
        </div>

        <div class="flex">

                <button type="button" class="flex items-center text-sm text-gray-500" @click="$inertia.visitInModal(route('advisors.suppliers.edit.state', {supplier: supplier.uuid}))">
                <TagIcon class="h-5 w-5 text-minplan-darkgreybeige" />
                <span class="hidden md:flex ml-1">
                    {{ $t("suppliers.stateChange") }}
                </span>
            </button>

        </div>
    </div>

    <!-- Project Section -->

    <h2 class="font-bold uppercase tracking-wider mt-6">{{ $t("suppliers.projects") }}</h2>

    <table class="min-w-full divide-y divide-gray-200 mt-2 md:mt-4">
        <thead class="bg-minplan-lightgreybeige">
            <tr>
                <th scope="col"
                    class="px-1 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-minplan-beige uppercase tracking-wider">
                    {{ $t("suppliers.projects") }}
                </th>
                <th scope="col"
                    class="px-1 md:px-6 py-2 md:py-3 text-left text-xs font-medium text-minplan-beige uppercase tracking-wider text-center">
                    {{ $t("suppliers.offers") }}
                </th>
                <th scope="col"></th>
            </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="project in projects" :key="project.i">
                <td class="px-1 md:px-6 py-2 md:py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                     {{ project.name }}
                </td>
                <td class="px-1 md:px-6 py-2 md:py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-center">
                    {{ project.offers}}
                </td>
                <td class="px-1 md:px-6 py-2 md:py-3 whitespace-nowrap text-sm text-right font-medium">
                    <Link :href="route('advisors.projects.show', project.uuid)" preserve-state>
                        <ChevronRightIcon class="ml-4 mr-2 h-5 w-5 text-gray-500 float-right" />
                    </Link>
                </td>
            </tr>
        </tbody>
    </table>



</template>

<script>
import { defineComponent } from "vue";
import MainLayout from "@/Layouts/MainLayout";
import {
    PlusSmIcon,
    UserIcon,
    PencilIcon,
    TruckIcon,
    TagIcon,
    ChevronRightIcon
} from "@heroicons/vue/outline";
import { Link } from "@inertiajs/inertia-vue3";
import PageTitle from "@/Shared/Components/PageTitle";
import { Head } from "@inertiajs/inertia-vue3";

export default defineComponent({
    props: ["supplier", "projects", "deletable"],
    layout: MainLayout,
    components: {
        Head,
        PlusSmIcon,
        UserIcon,
        PencilIcon,
        TruckIcon,
        TagIcon,
        ChevronRightIcon,
        Link,
        PageTitle
    },
    computed: {
        // Thema 9 (Welle 4, Paket c): Anzeige-Fallback — fehlt einer
        // Kontaktperson E-Mail/Telefon, zeigt die Seite die Firmendaten des
        // Unternehmers, mit Kennzeichnung (Entscheid Luca, BACKLOG §4.4).
        //
        // R3 Punkt 2 (BACKLOG §5 Welle-4 (h)): `resolvedEmail`/
        // `resolvedPhone` statt überschriebenem `email`/`phone` — dasselbe
        // Namensschema wie `TopicShowResource::supplierContactBlock()` und
        // `ContactPicker.vue`. Die BERECHNUNG bleibt eine eigene: diese
        // Liste deckt ALLE Kontakte des Unternehmers (`SupplierResource`),
        // nicht die eine gewählte Person des Themen-Kontexts — keine
        // 1:1-Extraktion ohne eigenen Auftrag (s. `ContactPicker.vue`-
        // Docblock).
        contactsWithFallback() {
            return (this.supplier.contacts || []).map((contact) => ({
                ...contact,
                resolvedEmail: contact.email || this.supplier.email || "",
                resolvedPhone: contact.phone || this.supplier.phone || "",
                emailIsFallback: !contact.email && Boolean(this.supplier.email),
                phoneIsFallback: !contact.phone && Boolean(this.supplier.phone)
            }));
        }
    },
    methods: {
    }
});
</script>

<style scoped></style>
